import type { InputHTMLAttributes } from "react";
import { useId } from "react";

export interface GfgTextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Muted, lower-emphasis suffix on the label (e.g. "— for order updates").
   *  Auto-hides once the label floats (on focus or when the field has content). */
  hint?: string;
  /** Red border for a failed-validation state. */
  error?: boolean;
  /** Message describing the error, announced to screen readers and shown below the
   *  field (wired via aria-describedby). Renders only when `error` is also true. */
  errorText?: string;
  /** Background the label notch sits on, so it stays clean on any surface. */
  surface?: "white" | "background";
  /** Extra classes merged onto the <input> (e.g. squared corners when fused). */
  inputClassName?: string;
  /** Tell password managers (1Password, LastPass, Bitwarden, Proton) to skip
   *  this field — for non-credential forms where their inline menu is noise. */
  ignorePasswordManagers?: boolean;
  /** Shows a "length / maxLength" counter below the field. Needs `maxLength` and a controlled `value`. */
  showCounter?: boolean;
}

/**
 * GfG canonical text field — floating label that rests centred, then notches
 * onto the top border on focus or when the input has content.
 * (Requires the input to keep placeholder=" " so :placeholder-shown drives the label.)
 */
export function GfgTextField({
  label,
  hint,
  error = false,
  errorText,
  surface = "white",
  className = "",
  inputClassName = "",
  ignorePasswordManagers = false,
  showCounter = false,
  id,
  ...rest
}: GfgTextFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errId = `${inputId}-err`;
  const showError = error && !!errorText;
  const surfaceCls = surface === "background" ? "bg-background" : "bg-white";
  const borderCls = error ? "border-good-wine" : "border-smoke";
  const pwIgnore = ignorePasswordManagers ? "true" : undefined;

  return (
    <div className={`group relative w-full ${className}`.trim()}>
      <input
        id={inputId}
        placeholder=" "
        aria-invalid={error || undefined}
        aria-describedby={showError ? errId : undefined}
        className={`peer w-full rounded-[8px] border ${borderCls} bg-white px-[14px] pt-[18px] pb-[8px] font-lato text-[14px] text-rodeo-black focus:border-dust-ink focus:outline-none ${inputClassName}`.trim()}
        {...rest}
        data-1p-ignore={pwIgnore}
        data-lpignore={pwIgnore}
        data-bwignore={pwIgnore}
        data-protonpass-ignore={pwIgnore}
      />
      <label
        htmlFor={inputId}
        className={
          `pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2 ${surfaceCls} px-[5px] text-[14px] text-graphite transition-all ` +
          // floated state (focused OR has content) — keep -translate-y-1/2 so the
          // label's centre notches onto the top border rather than sitting below it:
          "peer-focus:top-0 peer-focus:text-[10px] peer-focus:font-bold peer-focus:uppercase peer-focus:tracking-[1.5px] peer-focus:text-dust-ink " +
          "peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-bold peer-[:not(:placeholder-shown)]:uppercase peer-[:not(:placeholder-shown)]:tracking-[1.5px] peer-[:not(:placeholder-shown)]:text-dust-ink"
        }
      >
        {label}
        {hint ? (
          <span className="ml-1 font-normal text-graphite group-has-[input:focus]:hidden group-has-[input:not(:placeholder-shown)]:hidden">
            {hint}
          </span>
        ) : null}
      </label>
      {showError || (showCounter && rest.maxLength) ? (
        <div className="mt-[6px] flex justify-between gap-3 px-[2px] font-lato text-[12px]">
          {showError ? (
            <p id={errId} className="text-good-wine">
              {errorText}
            </p>
          ) : (
            <span />
          )}
          {showCounter && rest.maxLength ? (
            <span className="ml-auto shrink-0 text-graphite" aria-hidden="true">
              {String(rest.value ?? "").length} / {rest.maxLength}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default GfgTextField;
